import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ScrollToTop from '@/components/ScrollToTop';
import ViewportMeta from '@/components/ViewportMeta';
import ActivityBackButton from '@/components/ActivityBackButton';
import ActivityImageGallery from '@/components/ActivityImageGallery';
import ActivityVideoGallery from '@/components/ActivityVideoGallery';
import { useLanguage } from '@/contexts/LanguageContext';
import { getActivity } from '@/data/activities';
import { useActivityCms } from '@/hooks/useActivityCms';
import { pickBilingual } from '@/services/contentApi';

interface ActivityPageProps {
  activityId: string;
}

const ActivityPage: React.FC<ActivityPageProps> = ({ activityId }) => {
  const { t, language } = useLanguage();
  const activity = getActivity(activityId);
  const [isGalleryExpanded, setIsGalleryExpanded] = useState(false);

  const { images, videoIds, content } = useActivityCms(activityId, {
    images: activity?.galleryImages ?? [],
    videoIds: activity?.youtubeVideoIds ?? [],
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activityId]);

  if (!activity || activityId === 'annathanam') {
    return <Navigate to="/" replace />;
  }

  const title = pickBilingual(content?.title, language, t(activity.titleKey));
  const galleryTitle = `${title} ${t('gallery')}`;
  const videoTitle = `${title} ${t('videos')}`;

  const overrideParagraphs = content?.paragraphs?.length
    ? content.paragraphs.map((p) => pickBilingual(p, language))
    : null;

  return (
    <>
      <ViewportMeta />
      <div className="min-h-screen flex flex-col">
        <Header />
        <ActivityBackButton
          isExpanded={isGalleryExpanded}
          onCloseExpanded={() => setIsGalleryExpanded(false)}
        />
        <main className="flex-grow pt-24 pb-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="p-8 md:p-12">
              <div className="text-center mb-10">
                <span className="text-6xl mb-4 block">{activity.icon}</span>
                <h1 className="text-4xl md:text-5xl font-bold text-[#1C3F75] font-serif mb-4">
                  {title}
                </h1>
                <div className="h-1 w-24 bg-primary-500 mx-auto rounded-full" />
              </div>

              <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
                {overrideParagraphs
                  ? overrideParagraphs.map((text, index) => (
                      <p key={index} className="leading-relaxed text-left">
                        {text}
                      </p>
                    ))
                  : activity.descriptionKeys.map((key, index) => {
                      const text = t(key);
                      const isQuote =
                        key === 'annathanamQuote' ||
                        key.endsWith('Quote') ||
                        key === 'annathanamQuote2';

                      if (isQuote && index === 0) {
                        return (
                          <p
                            key={key}
                            className="text-xl font-medium text-center text-[#1C3F75] italic mb-8"
                          >
                            &ldquo;{text}&rdquo;
                          </p>
                        );
                      }

                      if (key === 'annathanamQuote2') {
                        return (
                          <p key={key} className="leading-relaxed text-left">
                            {t('annathanamDesc2')}
                            <br />
                            <span className="font-semibold block mt-2 text-center">
                              &ldquo;{text}&rdquo;
                            </span>
                          </p>
                        );
                      }

                      return (
                        <p key={key} className="leading-relaxed text-left">
                          {text}
                        </p>
                      );
                    })}
              </div>
            </div>
          </div>

          <ActivityVideoGallery
            videoIds={videoIds}
            sectionTitle={videoTitle}
            activityLabel={title}
          />

          <ActivityImageGallery
            images={images}
            galleryTitle={galleryTitle}
            imageAltPrefix={title}
            onExpandedChange={setIsGalleryExpanded}
          />
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
};

export default ActivityPage;
